import type {
  ExpressionDiagnostic,
  ExpressionFunction,
  ExprNode,
  MatchPattern,
} from './ast.js';
import { getAllCustomFunctions } from './custom-functions.js';
import { knownFunctionNames } from './known-functions.js';
import {
  type ExampleValue,
  parseExpressionDetailed,
  typeOfExample,
} from './parser-detailed.js';

/**
 * A database a Crown expression can be compiled for.
 *
 * NAMING NOTE FOR THE WHOLE FILE: the `Sql*` names came first, when Postgres was
 * the only target. SurrealDB is not SQL, so the `Query*` names were introduced
 * as the neutral spelling and the `Sql*` ones kept as aliases. They are the same
 * types and the same functions - prefer the `Query*` spelling in new code.
 */
export type SqlDialect = 'postgres' | 'surreal';
/** What a compiled fragment evaluates to, so the compiler can insert casts and pick the right comparison. */
export type SqlValueType =
  | 'boolean'
  | 'number'
  | 'string'
  | 'array'
  | 'object'
  | 'date'
  | 'datetime'
  | 'null'
  | 'unknown';
/** The neutral spelling of {@link SqlDialect}. */
export type QueryTargetKind = SqlDialect;
/** Whether a target is relational or document-oriented, which decides how a path into a nested value is written. */
export type QueryTargetFamily = 'sql' | 'nosql';

/**
 * What the expression roots become in the generated query.
 *
 * A Crown expression names `root`, `value` and `ctx`; a query has to name a
 * table, a column or a bound parameter instead. This is that mapping, and it has
 * to be supplied by the caller because only the caller knows the shape being
 * queried.
 */
export interface SqlReferenceConfig {
  root?: string;
  value?: string;
  ctx?: string;
}

/**
 * One thing an expression does that the target cannot express.
 *
 * Not every Crown expression has a query equivalent - dynamic property access
 * and `ctx` lookups usually do not, since neither is knowable at compile time.
 * These are collected rather than thrown so a caller can see everything that
 * would need changing, not just the first thing.
 */
export interface SqlUnsupportedFeature {
  code:
    | 'parse-error'
    | 'unsupported-node'
    | 'unsupported-function'
    | 'unsupported-pattern'
    | 'unsupported-dynamic-access'
    | 'unsupported-context'
    | 'unsupported-expression';
  message: string;
  adapter: SqlDialect;
  nodeKind?: ExprNode['kind'];
  functionName?: string;
}

/** A piece of generated query text with the type it evaluates to. */
export interface SqlFragment {
  sql: string;
  type: SqlValueType;
}

/** The neutral spelling of {@link SqlFragment}. */
export type QueryFragment = SqlFragment;

/**
 * The outcome of compiling one expression.
 *
 * CHECK `unsupported` BEFORE USING THE QUERY. Compilation does not fail on an
 * untranslatable construct - it records it and carries on - so a result can hold
 * query text that does not mean what the expression meant. A non-empty
 * `unsupported` means fall back to evaluating in the application.
 */
export interface SqlCompilationResult {
  source: string;
  sql: string;
  ast?: ExprNode;
  diagnostics: ExpressionDiagnostic[];
  unsupported: SqlUnsupportedFeature[];
  supported: boolean;
  parameters: ExampleValue[] | Record<string, ExampleValue>;
  parameterOrder: string[];
  adapter: SqlDialect;
}

/** What a target can and cannot translate, for deciding before compiling whether the attempt is worth making. */
export interface SqlSupportReport {
  adapter: SqlDialect;
  family: QueryTargetFamily;
  supportedFunctions: string[];
  unsupportedFunctions: Array<{ name: string; reason: string }>;
  unsupportedNodeKinds: ExprNode['kind'][];
}

/** The neutral spelling of {@link SqlSupportReport}. */
export type QueryTranslationSupportReport = SqlSupportReport;

/** What a function translator is given: the function's name, its already-compiled arguments, and the adapter in use. */
export interface QueryFunctionContext {
  name: string;
  args: QueryFragment[];
  state: EmitState;
  expectedType: SqlValueType;
}

/**
 * Turns one Crown function call into target query text.
 *
 * Returning `undefined` means "I cannot translate this", which the compiler
 * records as unsupported rather than treating as an error - so a translator can
 * decline particular argument shapes while handling the rest.
 */
export type QueryFunctionTranslator = (
  context: QueryFunctionContext,
) => QueryFragment | undefined;

/** The simple case: a Crown function that is just a rename in the target. */
export interface QueryFunctionMapping {
  target: string;
  returnType?: SqlValueType;
}

/**
 * Everything that differs between one target and another.
 *
 * Adapters exist so the compiler holds the shared work - walking the AST,
 * precedence, diagnostics - while quoting, function names and path syntax stay
 * per-target. Add a database by writing one of these, not by editing the
 * compiler.
 */
export interface ExpressionQueryAdapter {
  kind: QueryTargetKind;
  family: QueryTargetFamily;
  defaultReferences: Required<SqlReferenceConfig>;
  createParameter(state: EmitState, value: ExampleValue): string;
  quoteString(value: string): string;
  accessPath(
    baseSql: string,
    segments: Array<string | number>,
    expectedType: SqlValueType,
  ): string;
  nullCheck(sql: string, equals: boolean): string;
  concat(parts: QueryFragment[]): string;
  binary(
    op: string,
    left: QueryFragment,
    right: QueryFragment,
    resultType: SqlValueType,
  ): string;
  inList(left: QueryFragment, right: QueryFragment, equals: boolean): string;
  functionCall(
    name: string,
    args: QueryFragment[],
    state: EmitState,
    expectedType: SqlValueType,
  ): QueryFragment | undefined;
  arrayLiteral(items: QueryFragment[]): string;
  /**
   * How the target spells "this, or that when it is nothing".
   *
   * Postgres has `COALESCE`; SurrealQL has the `??` operator and no COALESCE at
   * all, so a query built with one is refused before it runs.
   */
  coalesce(left: QueryFragment, right: QueryFragment): string;
  /**
   * How the target spells a choice.
   *
   * Postgres has `CASE WHEN … THEN … ELSE … END`; SurrealQL has
   * `IF … THEN … ELSE … END` and does not accept the other. Each branch's
   * `result` is its THEN part; it is not called `then`, because an object
   * with a `then` key is a thenable and `await` would treat it as a promise.
   */
  conditional(
    branches: Array<{ when: QueryFragment; result: QueryFragment }>,
    otherwise: QueryFragment,
  ): string;
  customFunctionTranslators?: Record<string, QueryFunctionTranslator>;
  unsupportedFunctions?: Map<string, string>;
}

/** The older spelling of {@link ExpressionQueryAdapter}. */
export type SqlAdapter = ExpressionQueryAdapter;

/** Which target to compile for, and how the expression roots map onto it. */
export interface CompileExpressionToSqlOptions {
  adapter?: QueryTargetKind | ExpressionQueryAdapter;
  references?: SqlReferenceConfig;
  strict?: boolean;
  customFunctionTranslators?: Record<string, QueryFunctionTranslator>;
  customFunctionMappings?: Record<string, QueryFunctionMapping>;
}

interface EmitState {
  adapter: ExpressionQueryAdapter;
  diagnostics: ExpressionDiagnostic[];
  unsupported: SqlUnsupportedFeature[];
  references: Required<SqlReferenceConfig>;
  strict: boolean;
  positionalParams: ExampleValue[];
  namedParams: Record<string, ExampleValue>;
  parameterOrder: string[];
  customFunctionTranslators: Record<string, QueryFunctionTranslator>;
}

type SqlEnv = Map<string, SqlFragment>;

/** Every name the engine knows, for the completion list this offers. */
function builtinFunctionNames(): string[] {
  return [...knownFunctionNames()].sort();
}

function buildTemplateTranslator(
  mapping: QueryFunctionMapping,
): QueryFunctionTranslator {
  return ({ args }) => ({
    sql: `${mapping.target}(${args.map((arg) => arg.sql).join(', ')})`,
    type: mapping.returnType ?? 'unknown',
  });
}

function buildCustomFunctionTranslators(
  options: CompileExpressionToSqlOptions,
): Record<string, QueryFunctionTranslator> {
  const translators = new Map<string, QueryFunctionTranslator>();
  // An adapter passed as an object may carry its own translators; one passed as
  // a name is a name.
  const fromAdapter =
    options.adapter instanceof Object
      ? options.adapter.customFunctionTranslators
      : undefined;
  for (const [name, translator] of Object.entries(fromAdapter ?? {})) {
    translators.set(name, translator);
  }
  for (const [name, translator] of Object.entries(
    options.customFunctionTranslators ?? {},
  )) {
    translators.set(name, translator);
  }

  if (options.customFunctionMappings) {
    for (const [name, mapping] of Object.entries(
      options.customFunctionMappings,
    )) {
      translators.set(name, buildTemplateTranslator(mapping));
    }
  }

  return Object.fromEntries(translators);
}

function getAllKnownFunctionNames(
  translators: Record<string, QueryFunctionTranslator>,
): string[] {
  return Array.from(
    new Set([
      ...builtinFunctionNames(),
      ...Object.keys(getAllCustomFunctions()),
      ...Object.keys(translators),
    ]),
  ).sort();
}

const POSTGRES_UNSUPPORTED_FUNCTIONS = new Map<string, string>([
  ['hash', 'async hashing cannot be represented as a pure SQL expression'],
  [
    'hashSync',
    'hash helpers are runtime-only and not portable across SQL dialects',
  ],
  [
    'sha256',
    'hash helpers are runtime-only and not portable across SQL dialects',
  ],
  ['md5', 'hash helpers are runtime-only and not portable across SQL dialects'],
  [
    'stringify',
    'JSON serialisation changes semantics and is not emitted automatically',
  ],
  ['parse', 'runtime type parsing cannot be represented faithfully in SQL'],
  ['parseDate', 'free-form parsing formats are not yet translated generically'],
  ['isEmpty', 'empty checks across arrays/objects need schema-aware typing'],
  ['isNotEmpty', 'empty checks across arrays/objects need schema-aware typing'],
  ['pluck', 'array/object projections require higher-order SQL translation'],
  ['compact', 'array filtering requires higher-order SQL translation'],
  ['range', 'range generation is not yet emitted generically'],
  ['where', 'higher-order callbacks are not translated to SQL yet'],
  ['map', 'higher-order callbacks are not translated to SQL yet'],
  ['find', 'higher-order callbacks are not translated to SQL yet'],
  ['any', 'higher-order callbacks are not translated to SQL yet'],
  ['all', 'higher-order callbacks are not translated to SQL yet'],
  ['sortBy', 'higher-order callbacks are not translated to SQL yet'],
  ['sumOf', 'higher-order callbacks are not translated to SQL yet'],
  ['averageOf', 'higher-order callbacks are not translated to SQL yet'],
  ['countWhere', 'higher-order callbacks are not translated to SQL yet'],
  ['fv', 'financial Excel helpers are not translated yet'],
  ['pv', 'financial Excel helpers are not translated yet'],
  ['pmt', 'financial Excel helpers are not translated yet'],
  ['npv', 'financial Excel helpers are not translated yet'],
  ['irr', 'financial Excel helpers are not translated yet'],
]);

const SURREAL_UNSUPPORTED_FUNCTIONS = new Map<string, string>([
  [
    'pow',
    'SurrealQL raises in binary floating point, and refuses a negative exponent',
  ],
  [
    'split',
    // Verified against SurrealDB 3.2.1: `split("001,2,003", ",")` is
    // ["001", 2, "003"] in the engine and ["001", "2", "003"] in the database.
    // Every string function in the engine turns a numeric-looking result into a
    // number, and SurrealQL has no faithful way to say that - casting a
    // non-numeric string to a number is an error there rather than nothing. A
    // wrong answer is worse than a translation that declines.
    'the engine reads a numeric-looking part as a number and SurrealQL keeps it a string',
  ],
  [
    'regexReplace',
    'regex replacement mapping has not been verified for SurrealQL yet',
  ],
  [
    'replaceAll',
    'global replace semantics have not been verified for SurrealQL yet',
  ],
  [
    'stripSpaces',
    'whitespace regex replacement is not emitted for SurrealQL yet',
  ],
  ['sqrt', 'square-root helper is not emitted for SurrealQL yet'],
  ['trunc', 'numeric truncation helper is not emitted for SurrealQL yet'],
  ['sign', 'numeric sign helper is not emitted for SurrealQL yet'],
  ['exp', 'exponential helper is not emitted for SurrealQL yet'],
  ['log', 'logarithm helper is not emitted for SurrealQL yet'],
  ['log10', 'base-10 logarithm helper is not emitted for SurrealQL yet'],
  ['sin', 'trigonometry helpers are not emitted for SurrealQL yet'],
  ['cos', 'trigonometry helpers are not emitted for SurrealQL yet'],
  ['tan', 'trigonometry helpers are not emitted for SurrealQL yet'],
  ['asin', 'trigonometry helpers are not emitted for SurrealQL yet'],
  ['acos', 'trigonometry helpers are not emitted for SurrealQL yet'],
  ['atan', 'trigonometry helpers are not emitted for SurrealQL yet'],
  ['atan2', 'trigonometry helpers are not emitted for SurrealQL yet'],
  ['radians', 'angle conversion helpers are not emitted for SurrealQL yet'],
  ['degrees', 'angle conversion helpers are not emitted for SurrealQL yet'],
  ['pi', 'constant helper is not emitted for SurrealQL yet'],
  ['e', 'constant helper is not emitted for SurrealQL yet'],
  [
    'random',
    'random helpers are not emitted for deterministic query generation',
  ],
  [
    'randomInt',
    'random helpers are not emitted for deterministic query generation',
  ],
  ['clamp', 'numeric helper is not emitted for SurrealQL yet'],
  ['lerp', 'numeric helper is not emitted for SurrealQL yet'],
  ['percentage', 'numeric helper is not emitted for SurrealQL yet'],
  ['median', 'median helper is not emitted for SurrealQL yet'],
  ['mode', 'mode helper is not emitted for SurrealQL yet'],
  ['stdev', 'standard deviation helper is not emitted for SurrealQL yet'],
  [
    'count',
    'count over arbitrary expressions is not emitted for SurrealQL yet',
  ],
  ['percentile', 'percentile helper is not emitted for SurrealQL yet'],
  ['first', 'array convenience helpers are not emitted for SurrealQL yet'],
  ['last', 'array convenience helpers are not emitted for SurrealQL yet'],
  ['nth', 'array convenience helpers are not emitted for SurrealQL yet'],
  ['at', 'array convenience helpers are not emitted for SurrealQL yet'],
  ['reverse', 'array helper is not emitted for SurrealQL yet'],
  ['unique', 'array helper is not emitted for SurrealQL yet'],
  ['flatten', 'array helper is not emitted for SurrealQL yet'],
  ['join', 'array helper is not emitted for SurrealQL yet'],
  ['concat', 'array concat helper is not emitted for SurrealQL yet'],
  ['slice', 'array slicing helper is not emitted for SurrealQL yet'],
  ['take', 'array slicing helper is not emitted for SurrealQL yet'],
  ['skip', 'array slicing helper is not emitted for SurrealQL yet'],
  ['isEmpty', 'empty checks across arrays/objects need schema-aware typing'],
  ['isNotEmpty', 'empty checks across arrays/objects need schema-aware typing'],
  ['pluck', 'array/object projections require higher-order translation'],
  ['compact', 'array filtering requires higher-order translation'],
  ['range', 'range generation is not emitted for SurrealQL yet'],
  ['countif', 'countif helper is not emitted for SurrealQL yet'],
  ['where', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['map', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['find', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['any', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['all', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['sortBy', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['sumOf', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['averageOf', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['countWhere', 'higher-order callbacks are not translated to SurrealQL yet'],
  ['timestamp', 'timestamp extraction helper is not emitted for SurrealQL yet'],
  ['yearsBetween', 'date-diff helper is not emitted for SurrealQL yet'],
  ['withinHours', 'date-diff helper is not emitted for SurrealQL yet'],
  ['withinMinutes', 'date-diff helper is not emitted for SurrealQL yet'],
  ['date', 'date construction helper is not emitted for SurrealQL yet'],
  ['dateValue', 'date conversion helper is not emitted for SurrealQL yet'],
  ['weekdayName', 'date part helper is not emitted for SurrealQL yet'],
  ['monthName', 'date part helper is not emitted for SurrealQL yet'],
  ['addMonths', 'date arithmetic helper is not emitted for SurrealQL yet'],
  ['addYears', 'date arithmetic helper is not emitted for SurrealQL yet'],
  ['endOfDay', 'date boundary helper is not emitted for SurrealQL yet'],
  ['startOfMonth', 'date boundary helper is not emitted for SurrealQL yet'],
  ['endOfMonth', 'date boundary helper is not emitted for SurrealQL yet'],
  ['daysInMonth', 'date helper is not emitted for SurrealQL yet'],
  ['isLeapYear', 'date helper is not emitted for SurrealQL yet'],
  ['quarterOfYear', 'date helper is not emitted for SurrealQL yet'],
  ['weekOfYear', 'date helper is not emitted for SurrealQL yet'],
  ['formatDate', 'date formatting helper is not emitted for SurrealQL yet'],
  ['parseDate', 'date parsing helper is not emitted for SurrealQL yet'],
  ['isBetween', 'date helper is not emitted for SurrealQL yet'],
  ['age', 'date helper is not emitted for SurrealQL yet'],
  ['businessDays', 'date helper is not emitted for SurrealQL yet'],
  [
    'stringify',
    'JSON serialisation changes semantics and is not emitted automatically',
  ],
  [
    'parse',
    'runtime type parsing cannot be represented faithfully in SurrealQL',
  ],
  ['parseInt', 'numeric parsing helper is not emitted for SurrealQL yet'],
  ['parseFloat', 'numeric parsing helper is not emitted for SurrealQL yet'],
  ['toInt', 'numeric parsing helper is not emitted for SurrealQL yet'],
  ['toNumber', 'numeric parsing helper is not emitted for SurrealQL yet'],
  ['Number', 'numeric parsing helper is not emitted for SurrealQL yet'],
  [
    'hash',
    'hash helpers are runtime-only and not portable across query engines',
  ],
  [
    'hashSync',
    'hash helpers are runtime-only and not portable across query engines',
  ],
  [
    'sha256',
    'hash helpers are runtime-only and not portable across query engines',
  ],
  [
    'md5',
    'hash helpers are runtime-only and not portable across query engines',
  ],
  ['pmt', 'financial helpers are not emitted for SurrealQL yet'],
  ['fv', 'financial helpers are not emitted for SurrealQL yet'],
  ['pv', 'financial helpers are not emitted for SurrealQL yet'],
  ['npv', 'financial helpers are not emitted for SurrealQL yet'],
  ['irr', 'financial helpers are not emitted for SurrealQL yet'],
]);

const POSTGRES_UNSUPPORTED_NODE_KINDS: ExprNode['kind'][] = [
  'debugger',
  'typeof',
  'arrow',
  'foreach',
  'template',
];
const SURREAL_UNSUPPORTED_NODE_KINDS: ExprNode['kind'][] = [
  'debugger',
  'typeof',
  'arrow',
  'foreach',
  'template',
];

function createUnsupportedFeature(
  state: EmitState,
  partial: Omit<SqlUnsupportedFeature, 'adapter'>,
): SqlUnsupportedFeature {
  const issue: SqlUnsupportedFeature = {
    ...partial,
    adapter: state.adapter.kind,
  };
  state.unsupported.push(issue);
  return issue;
}

function literalType(value: ExampleValue | undefined): SqlValueType {
  // The expression language already classifies a value; this is the same
  // question in the query's vocabulary, where a date is a datetime and a
  // currency is the number it carries.
  const kind = typeOfExample(value);
  if (kind === 'date') return 'datetime';
  if (kind === 'currency') return 'number';
  return kind;
}

function mergeExpectedType(
  expected: SqlValueType,
  fallback: SqlValueType,
): SqlValueType {
  return expected === 'unknown' ? fallback : expected;
}

function isNullishLiteral(node: ExprNode): boolean {
  return (
    node.kind === 'literal' && (node.value === null || node.value === undefined)
  );
}

function compileLiteral(
  value: ExampleValue | undefined,
  state: EmitState,
): SqlFragment {
  const type = literalType(value);
  // A date crosses as the text the target parses; everything else crosses as
  // itself, and nothing at all crosses as null.
  if (type === 'datetime' && value instanceof Date) {
    return {
      sql: state.adapter.createParameter(state, value.toISOString()),
      type,
    };
  }
  return {
    sql: state.adapter.createParameter(state, value ?? null),
    type,
  };
}

function compileArrayLiteral(
  node: Extract<ExprNode, { kind: 'array' }>,
  state: EmitState,
  env: SqlEnv,
): SqlFragment {
  const items = node.elements.map((element) =>
    emitNode(element, state, env, 'unknown'),
  );
  return {
    sql: state.adapter.arrayLiteral(items),
    type: 'array',
  };
}

function compileIdentifier(
  node: Extract<ExprNode, { kind: 'identifier' }>,
  state: EmitState,
  env: SqlEnv,
  expectedType: SqlValueType,
): SqlFragment {
  const [head, ...rest] = node.chain;
  if (!head) return { sql: 'NULL', type: 'unknown' };

  const bound = env.get(head);
  if (bound) {
    if (rest.length === 0) return bound;
    return {
      sql: state.adapter.accessPath(bound.sql, rest, expectedType),
      type: expectedType,
    };
  }

  if (head === 'value') {
    if (rest.length === 0)
      return {
        sql: state.adapter.accessPath(state.references.value, [], expectedType),
        type: expectedType,
      };
    return {
      sql: state.adapter.accessPath(state.references.value, rest, expectedType),
      type: expectedType,
    };
  }

  if (head === 'root') {
    if (rest.length === 0)
      return {
        sql: state.adapter.accessPath(state.references.root, [], expectedType),
        type: expectedType,
      };
    return {
      sql: state.adapter.accessPath(state.references.root, rest, expectedType),
      type: expectedType,
    };
  }

  if (head === 'ctx') {
    if (!state.references.ctx) {
      createUnsupportedFeature(state, {
        code: 'unsupported-context',
        message:
          'ctx.* access requires a SQL reference mapping in compile options',
        nodeKind: 'identifier',
      });
      return { sql: 'NULL', type: 'unknown' };
    }
    if (rest.length === 0)
      return {
        sql: state.adapter.accessPath(state.references.ctx, [], expectedType),
        type: expectedType,
      };
    return {
      sql: state.adapter.accessPath(state.references.ctx, rest, expectedType),
      type: expectedType,
    };
  }

  return {
    sql: state.adapter.accessPath(
      state.references.root,
      node.chain,
      expectedType,
    ),
    type: expectedType,
  };
}

function compileSpecialCall(
  node: Extract<ExprNode, { kind: 'call' }>,
  state: EmitState,
  env: SqlEnv,
  expectedType: SqlValueType,
): SqlFragment | undefined {
  if (node.name === '__member_access__') {
    const objectExpr = node.args[0]
      ? emitNode(node.args[0], state, env, 'unknown')
      : { sql: 'NULL', type: 'unknown' };
    const propertyNode = node.args[1];
    // SAFETY: as elsewhere in this file - a literal's value is the JSON the
    // parser read out of the source.
    const propertyType =
      propertyNode?.kind === 'literal'
        ? literalType(propertyNode.value as ExampleValue)
        : 'unknown';
    if (
      !propertyNode ||
      propertyNode.kind !== 'literal' ||
      (propertyType !== 'string' && propertyType !== 'number')
    ) {
      createUnsupportedFeature(state, {
        code: 'unsupported-dynamic-access',
        message:
          'Dynamic member access can only be translated when the property/index is a literal',
        nodeKind: 'call',
      });
      return { sql: 'NULL', type: expectedType };
    }

    return {
      sql: state.adapter.accessPath(
        objectExpr.sql,
        // SAFETY: the guard above admits only a literal whose value is text or
        // a number, which is what a path segment is.
        [propertyNode.value as string | number],
        expectedType,
      ),
      type: expectedType,
    };
  }

  if (node.name === '__member_call__') {
    createUnsupportedFeature(state, {
      code: 'unsupported-expression',
      message: 'Dynamic member calls are not translated to SQL',
      nodeKind: 'call',
    });
    return { sql: 'NULL', type: expectedType };
  }

  return undefined;
}

function compileFunctionCall(
  node: Extract<ExprNode, { kind: 'call' }>,
  state: EmitState,
  env: SqlEnv,
  expectedType: SqlValueType,
): SqlFragment {
  const special = compileSpecialCall(node, state, env, expectedType);
  if (special) return special;

  const args = node.args.map((arg) => emitNode(arg, state, env, 'unknown'));

  // An own property only: a rule calling `toString` or `valueOf` found
  // Object.prototype's, ran it, and emitted `undefined` as SQL.
  const customTranslator = Object.hasOwn(
    state.customFunctionTranslators,
    node.name,
  )
    ? state.customFunctionTranslators[node.name]
    : undefined;
  if (customTranslator) {
    const compiled = customTranslator({
      name: node.name,
      args,
      state,
      expectedType,
    });
    if (compiled) return compiled;
  }

  const compiled = state.adapter.functionCall(
    node.name,
    args,
    state,
    expectedType,
  );
  if (compiled) return compiled;

  createUnsupportedFeature(state, {
    code: 'unsupported-function',
    message: `Function '${node.name}' cannot currently be translated for ${state.adapter.kind}`,
    functionName: node.name,
    nodeKind: 'call',
  });
  return { sql: 'NULL', type: expectedType };
}

function compileBinary(
  node: Extract<ExprNode, { kind: 'binary' }>,
  state: EmitState,
  env: SqlEnv,
  expectedType: SqlValueType,
): SqlFragment {
  if (node.op === '||' || node.op === '&&') {
    const left = emitNode(node.left, state, env, 'boolean');
    const right = emitNode(node.right, state, env, 'boolean');
    return {
      sql: state.adapter.binary(node.op, left, right, 'boolean'),
      type: 'boolean',
    };
  }

  if (node.op === '??') {
    const left = emitNode(node.left, state, env, expectedType);
    const right = emitNode(
      node.right,
      state,
      env,
      mergeExpectedType(expectedType, left.type),
    );
    return {
      sql: state.adapter.coalesce(left, right),
      type: left.type === 'unknown' ? right.type : left.type,
    };
  }

  if (['==', '!=', '===', '!=='].includes(node.op)) {
    if (isNullishLiteral(node.left)) {
      const right = emitNode(node.right, state, env, 'unknown');
      return {
        sql: state.adapter.nullCheck(
          right.sql,
          node.op === '==' || node.op === '===',
        ),
        type: 'boolean',
      };
    }
    if (isNullishLiteral(node.right)) {
      const left = emitNode(node.left, state, env, 'unknown');
      return {
        sql: state.adapter.nullCheck(
          left.sql,
          node.op === '==' || node.op === '===',
        ),
        type: 'boolean',
      };
    }
    // Typed from the literal on either side, if there is one. The context's
    // expectation is the RESULT's - inside `&&` that is boolean - and typing
    // the operands from it cast `root.status` to boolean in
    // `root.score >= 5 && root.status == "open"`.
    const operandType = guessComparisonType(node);
    const left = emitNode(node.left, state, env, operandType);
    const right = emitNode(node.right, state, env, operandType);
    return {
      sql: state.adapter.binary(
        node.op === '===' ? '=' : node.op === '!==' ? '!=' : node.op,
        left,
        right,
        'boolean',
      ),
      type: 'boolean',
    };
  }

  if (['>', '>=', '<', '<='].includes(node.op)) {
    const comparisonType = guessComparisonType(node);
    let left = emitNode(node.left, state, env, comparisonType);
    let right = emitNode(node.right, state, env, comparisonType);
    if (comparisonType === 'unknown') {
      if (left.type !== 'unknown' && right.type === 'unknown') {
        right = emitNode(node.right, state, env, left.type);
      } else if (right.type !== 'unknown' && left.type === 'unknown') {
        left = emitNode(node.left, state, env, right.type);
      }
    }
    return {
      sql: state.adapter.binary(node.op, left, right, 'boolean'),
      type: 'boolean',
    };
  }

  if (node.op === 'in' || node.op === 'not in') {
    const left = emitNode(node.left, state, env, 'unknown');
    const right = emitNode(node.right, state, env, 'array');
    return {
      sql: state.adapter.inList(left, right, node.op === 'in'),
      type: 'boolean',
    };
  }

  if (node.op === '+') {
    const left = emitNode(node.left, state, env, expectedType);
    const right = emitNode(node.right, state, env, expectedType);
    const resultType: SqlValueType =
      left.type === 'string' ||
      right.type === 'string' ||
      expectedType === 'string'
        ? 'string'
        : 'number';
    if (resultType === 'string') {
      // One side a string and the other a number is not concatenation in this
      // language: the engine reads a numeric-looking string as a number, so
      // `substring("abc100", 4) + 5` is 105, where SurrealQL concatenates to
      // "1005". Neither target can be made to answer the other's result from
      // the text alone, so the translation declines rather than differ.
      if (
        (left.type === 'string' && right.type === 'number') ||
        (left.type === 'number' && right.type === 'string')
      ) {
        state.unsupported.push({
          code: 'unsupported-pattern',
          message:
            "'+' between a string and a number: the engine reads a numeric string as a number and the target concatenates",
          adapter: state.adapter.kind,
          nodeKind: node.kind,
        });
      }
      return {
        sql: state.adapter.concat([left, right]),
        type: 'string',
      };
    }
    return {
      sql: state.adapter.binary('+', left, right, 'number'),
      type: 'number',
    };
  }

  if (['-', '*', '/', '%', '**'].includes(node.op)) {
    const left = emitNode(node.left, state, env, 'number');
    const right = emitNode(node.right, state, env, 'number');
    return {
      sql: state.adapter.binary(node.op, left, right, 'number'),
      type: 'number',
    };
  }

  createUnsupportedFeature(state, {
    code: 'unsupported-expression',
    message: `Operator '${node.op}' is not translated to SQL`,
    nodeKind: 'binary',
  });
  return { sql: 'NULL', type: expectedType };
}

/** What one match arm compiled to: the test, and what it binds for its body. */
interface CompiledPattern {
  /** The condition, absent when the pattern cannot be tested at all. */
  condition?: SqlFragment;
  /** The environment the arm's body is compiled in, when it binds a name. */
  bindings?: SqlEnv;
}

function compilePatternCondition(
  pattern: MatchPattern,
  matchExpr: SqlFragment,
  state: EmitState,
  env: SqlEnv,
): CompiledPattern {
  switch (pattern.kind) {
    case 'wildcard':
      return { condition: { sql: 'TRUE', type: 'boolean' }, bindings: env };
    case 'binding_pat': {
      const bindings = new Map(env);
      bindings.set(pattern.name, matchExpr);
      return { condition: { sql: 'TRUE', type: 'boolean' }, bindings };
    }
    case 'literal_pat': {
      const patternExpr = emitNode(pattern.expr, state, env, matchExpr.type);
      return {
        condition: {
          sql: state.adapter.binary('=', matchExpr, patternExpr, 'boolean'),
          type: 'boolean',
        },
        bindings: env,
      };
    }
    case 'range_pat': {
      const from = emitNode(
        pattern.from,
        state,
        env,
        matchExpr.type === 'unknown' ? 'number' : matchExpr.type,
      );
      const to = emitNode(
        pattern.to,
        state,
        env,
        matchExpr.type === 'unknown' ? 'number' : matchExpr.type,
      );
      const lower = state.adapter.binary('>=', matchExpr, from, 'boolean');
      const upper = state.adapter.binary(
        pattern.inclusive ? '<=' : '<',
        matchExpr,
        to,
        'boolean',
      );
      return {
        condition: { sql: `(${lower} AND ${upper})`, type: 'boolean' },
        bindings: env,
      };
    }
    case 'or_pat': {
      const parts: string[] = [];
      let bindings = env;
      for (const subPattern of pattern.patterns) {
        const compiled = compilePatternCondition(
          subPattern,
          matchExpr,
          state,
          bindings,
        );
        if (compiled.condition) parts.push(compiled.condition.sql);
        if (compiled.bindings) bindings = compiled.bindings;
      }
      return {
        condition: {
          sql: parts.length ? `(${parts.join(' OR ')})` : 'FALSE',
          type: 'boolean',
        },
        bindings,
      };
    }
  }
}

function compileSequence(
  node: Extract<ExprNode, { kind: 'sequence' }>,
  state: EmitState,
  env: SqlEnv,
  expectedType: SqlValueType,
): SqlFragment {
  const localEnv = new Map(env);
  let last: SqlFragment = { sql: 'NULL', type: expectedType };
  for (const statement of node.statements) {
    last = emitNode(statement, state, localEnv, expectedType);
  }
  return last;
}

function emitNode(
  node: ExprNode,
  state: EmitState,
  env: SqlEnv,
  expectedType: SqlValueType,
): SqlFragment {
  switch (node.kind) {
    case 'literal':
      // SAFETY: a literal node's value is whatever the parser read out of the
      // source - a string, a number, a boolean or null - which is what
      // ExampleValue describes. The AST types it loosely because that union is
      // shared with the engine's own tree.
      return compileLiteral(node.value as ExampleValue, state);
    case 'identifier':
      return compileIdentifier(node, state, env, expectedType);
    case 'unary': {
      const argType: SqlValueType = node.op === '!' ? 'boolean' : 'number';
      const arg = emitNode(node.arg, state, env, argType);
      if (node.op === '!')
        return { sql: `(NOT (${arg.sql}))`, type: 'boolean' };
      return { sql: `(-(${arg.sql}))`, type: 'number' };
    }
    case 'binary':
      return compileBinary(node, state, env, expectedType);
    case 'ternary': {
      const cond = emitNode(node.cond, state, env, 'boolean');
      const thenExpr = emitNode(node.then, state, env, expectedType);
      const elseExpr = emitNode(
        node.else,
        state,
        env,
        thenExpr.type === 'unknown' ? expectedType : thenExpr.type,
      );
      return {
        sql: state.adapter.conditional(
          [{ when: cond, result: thenExpr }],
          elseExpr,
        ),
        type: thenExpr.type === elseExpr.type ? thenExpr.type : expectedType,
      };
    }
    case 'call':
      return compileFunctionCall(node, state, env, expectedType);
    case 'sequence':
      return compileSequence(node, state, env, expectedType);
    case 'assignment': {
      const value = emitNode(node.value, state, env, expectedType);
      env.set(node.name, value);
      return value;
    }
    case 'array':
      return compileArrayLiteral(node, state, env);
    case 'object':
      createUnsupportedFeature(state, {
        code: 'unsupported-node',
        message: 'Object literal SQL translation is not implemented yet',
        nodeKind: 'object',
      });
      return { sql: 'NULL', type: 'object' };
    case 'pipeline': {
      const localEnv = new Map(env);
      const [firstStage, ...remainingStages] = node.stages;
      if (!firstStage) {
        return { sql: 'NULL', type: expectedType };
      }
      let current = emitNode(firstStage, state, localEnv, expectedType);
      for (const stage of remainingStages) {
        if (stage.kind === 'call') {
          const compiled = state.adapter.functionCall(
            stage.name,
            [
              current,
              ...stage.args.map((arg) =>
                emitNode(arg, state, localEnv, 'unknown'),
              ),
            ],
            state,
            expectedType,
          );
          if (!compiled) {
            createUnsupportedFeature(state, {
              code: 'unsupported-function',
              message: `Pipeline stage '${stage.name}' cannot currently be translated for ${state.adapter.kind}`,
              functionName: stage.name,
              nodeKind: 'pipeline',
            });
            return { sql: 'NULL', type: expectedType };
          }
          current = compiled;
          continue;
        }
        const soleChainSegment =
          stage.kind === 'identifier' && stage.chain.length === 1
            ? stage.chain[0]
            : undefined;
        if (soleChainSegment !== undefined) {
          const name = soleChainSegment;
          const compiled = state.adapter.functionCall(
            name,
            [current],
            state,
            expectedType,
          );
          if (!compiled) {
            createUnsupportedFeature(state, {
              code: 'unsupported-function',
              message: `Pipeline stage '${name}' cannot currently be translated for ${state.adapter.kind}`,
              functionName: name,
              nodeKind: 'pipeline',
            });
            return { sql: 'NULL', type: expectedType };
          }
          current = compiled;
          continue;
        }
        const pipeEnv = new Map(localEnv);
        pipeEnv.set('$', current);
        current = emitNode(stage, state, pipeEnv, expectedType);
      }
      return current;
    }
    case 'match': {
      const matchExpr = emitNode(node.expr, state, env, expectedType);
      const branches: Array<{ when: QueryFragment; result: QueryFragment }> =
        [];
      for (const candidate of node.cases) {
        const compiledPattern = compilePatternCondition(
          candidate.pattern,
          matchExpr,
          state,
          env,
        );
        if (!compiledPattern.condition) continue;
        let when = compiledPattern.condition;
        const branchEnv = compiledPattern.bindings ?? env;
        if (candidate.guard) {
          const guard = emitNode(candidate.guard, state, branchEnv, 'boolean');
          when = { sql: `(${when.sql} AND ${guard.sql})`, type: 'boolean' };
        }
        branches.push({
          when,
          result: emitNode(candidate.body, state, branchEnv, expectedType),
        });
      }
      return {
        sql: branches.length
          ? state.adapter.conditional(branches, { sql: 'NULL', type: 'null' })
          : 'NULL',
        type: expectedType,
      };
    }
    case 'debugger':
    case 'typeof':
    case 'arrow':
    case 'foreach':
    case 'template':
      createUnsupportedFeature(state, {
        code: 'unsupported-node',
        message: `AST node '${node.kind}' is not translated to SQL`,
        nodeKind: node.kind,
      });
      return { sql: 'NULL', type: expectedType };
  }
}

function guessComparisonType(
  node: Extract<ExprNode, { kind: 'binary' }>,
): SqlValueType {
  if (node.left.kind === 'literal') {
    // SAFETY: a literal's value is the JSON the parser read out of the source.
    return literalType(node.left.value as ExampleValue);
  }
  if (node.right.kind === 'literal') {
    // SAFETY: the same, on the other side of the comparison.
    return literalType(node.right.value as ExampleValue);
  }
  return 'unknown';
}

/**
 * Postgres: every value the rule reads is `jsonb`.
 *
 * `root`, `value` and `ctx` are `jsonb` documents, a field read without a
 * type stays `jsonb`, and a list - written or read - is a `jsonb` array. Each
 * operation converts what it needs, explicitly: text with `#>> '{}'`, numbers
 * through `numeric`, dates through a cast that answers null for text that is
 * not a date, as the engine does, where a bare cast would fail the query.
 *
 * It translates only what `conformance/postgres-parity.ts` shows answering what
 * the engine answers, and declines the rest; declining is not failing - the
 * caller then filters with the engine itself.
 */
function createPostgresAdapter(): ExpressionQueryAdapter {
  const castForType = (sql: string, type: SqlValueType) => {
    switch (type) {
      case 'number':
        return `CAST(${sql} AS numeric)`;
      case 'boolean':
        return `CAST(${sql} AS boolean)`;
      case 'date':
      case 'datetime':
        return timestamp(sql);
      default:
        return sql;
    }
  };

  /**
   * Text as a timestamp, or null when it is not one - the engine's answer.
   *
   * Text with an offset or `Z` is that instant. Text without one is UTC, as
   * the engine reads it - not the session's time zone, which is what a bare
   * cast to `timestamptz` would use.
   */
  const timestamp = (text: string) =>
    `(CASE WHEN ${text} ~ '(Z|[+-][0-9]{2}(:?[0-9]{2})?)$' AND pg_input_is_valid(${text}, 'timestamptz') THEN CAST(${text} AS timestamptz) WHEN pg_input_is_valid(${text}, 'timestamp') THEN CAST(${text} AS timestamp) AT TIME ZONE 'UTC' END)`;

  /** A fragment as text: `jsonb` unwrapped, anything else cast. */
  const txt = (a: QueryFragment | undefined) => {
    if (!a) return 'NULL';
    if (a.type === 'string') return a.sql;
    if (a.type === 'unknown' || a.type === 'array' || a.type === 'object')
      return `(${a.sql} #>> '{}')`;
    return `CAST(${a.sql} AS text)`;
  };
  /** A fragment as a number. */
  const num = (a: QueryFragment | undefined) => {
    if (!a) return 'NULL';
    if (a.type === 'number') return a.sql;
    return `CAST(${txt(a)} AS numeric)`;
  };
  /** A fragment as an instant: a number is milliseconds since 1970. */
  const instant = (a: QueryFragment | undefined) => {
    if (!a) return 'NULL';
    if (a.type === 'number') return `to_timestamp(${a.sql} / 1000.0)`;
    if (a.type === 'datetime' || a.type === 'date') return a.sql;
    return timestamp(txt(a));
  };
  /** A fragment as a time of day in UTC, without a zone. */
  const utc = (a: QueryFragment | undefined) =>
    `(${instant(a)} AT TIME ZONE 'UTC')`;
  /** A fragment as `jsonb`. */
  const json = (a: QueryFragment | undefined) => {
    if (!a || a.sql === 'NULL') return "'null'::jsonb";
    if (a.type === 'unknown' || a.type === 'array' || a.type === 'object')
      return a.sql;
    return `to_jsonb(${a.sql})`;
  };
  /** Whether a fragment is a list, to the extent the tree says. */
  const isList = (a: QueryFragment | undefined) => a?.type === 'array';
  /** The numbers of a `jsonb` list, as a set a subquery can aggregate. */
  const elements = (a: QueryFragment) => `jsonb_array_elements(${json(a)})`;

  const decline = (state: EmitState, name: string, why: string): undefined => {
    createUnsupportedFeature(state, {
      code: 'unsupported-function',
      message: why,
      functionName: name,
      nodeKind: 'call',
    });
    return undefined;
  };

  const pg: SqlAdapter = {
    kind: 'postgres',
    family: 'sql',
    defaultReferences: { root: 'root', value: 'value', ctx: 'ctx' },
    createParameter(state, value) {
      // Nothing crosses as the literal it is: an untyped null parameter is one
      // Postgres cannot infer a type for.
      if (value === null || value === undefined) return 'NULL';
      state.positionalParams.push(value);
      const placeholder = `$${state.positionalParams.length}`;
      state.parameterOrder.push(placeholder);
      // Typed where it is bound, so an operator or function never meets a
      // parameter of unknown type - and a number is `numeric`, the language's
      // decimal, not the double a driver binds it as.
      if (typeof value === 'number') return `CAST(${placeholder} AS numeric)`;
      if (typeof value === 'boolean') return `CAST(${placeholder} AS boolean)`;
      if (typeof value === 'string') return `CAST(${placeholder} AS text)`;
      // Money is an amount with a symbol the engine keeps apart from bare
      // numbers; nothing in Postgres keeps it apart, so it is declined.
      createUnsupportedFeature(state, {
        code: 'unsupported-expression',
        message:
          'money and other structured literals are not translated for Postgres',
        nodeKind: 'literal',
      });
      return `CAST(${placeholder} AS jsonb)`;
    },
    quoteString(value) {
      return `'${value.replace(/'/g, "''")}'`;
    },
    accessPath(baseSql, segments, expectedType) {
      // One step at a time, as `jsonb`: a name is `-> 'name'`, a position is
      // `-> n` - counted from 0, as `xs[0]` counts - and `first` and `last`
      // are the ends of a list but members of an object.
      const step = (current: string, segment: string | number) => {
        if (typeof segment === 'number')
          return `(${current} -> ${Math.trunc(segment)})`;
        const key = pg.quoteString(segment);
        if (segment === 'first' || segment === 'last') {
          const end = segment === 'first' ? '0' : '-1';
          return `(CASE WHEN jsonb_typeof(${current}) = 'array' THEN ${current} -> ${end} ELSE ${current} -> ${key} END)`;
        }
        return `(${current} -> ${key})`;
      };
      const path = segments.reduce<string>(step, baseSql);
      if (
        expectedType === 'unknown' ||
        expectedType === 'array' ||
        expectedType === 'object'
      )
        return path;
      return castForType(`(${path} #>> '{}')`, expectedType);
    },
    nullCheck(sql, equals) {
      // A `jsonb` null is a value, not SQL's NULL: both mean nothing here.
      const nothing = `(${sql} IS NULL OR ${sql} = 'null'::jsonb)`;
      return equals ? nothing : `(NOT ${nothing})`;
    },
    concat(parts) {
      // Each part as text: a `jsonb` string kept its quotes.
      return `CONCAT(${parts.map(txt).join(', ')})`;
    },
    binary(op, left, right) {
      if (op === '&&') return `(${left.sql} AND ${right.sql})`;
      if (op === '||') return `(${left.sql} OR ${right.sql})`;
      if (op === '**') return `POWER(${num(left)}, ${num(right)})`;
      if (['+', '-', '*', '/', '%'].includes(op))
        return `(${num(left)} ${op} ${num(right)})`;
      const sqlOp =
        op === '==' || op === '==='
          ? '='
          : op === '!=' || op === '!=='
            ? '<>'
            : op;
      // A `jsonb` side meets a typed one as that type; two `jsonb` sides
      // compare as `jsonb`, which orders numbers as numbers.
      const as = (side: QueryFragment, other: QueryFragment) => {
        if (side.type !== 'unknown' || other.type === 'unknown')
          return side.sql;
        if (other.type === 'string') return txt(side);
        if (other.type === 'number') return num(side);
        if (other.type === 'boolean') return `CAST(${txt(side)} AS boolean)`;
        if (other.type === 'date' || other.type === 'datetime')
          return instant(side);
        return side.sql;
      };
      return `(${as(left, right)} ${sqlOp} ${as(right, left)})`;
    },
    inList(left, right, equals) {
      const holds = `(${json(right)} @> jsonb_build_array(${left.sql}))`;
      return equals ? holds : `(NOT ${holds})`;
    },
    functionCall(name, args, state) {
      const reason = POSTGRES_UNSUPPORTED_FUNCTIONS.get(name);
      if (reason) return decline(state, name, reason);
      const [a, b, c] = args;
      const text = (fn: string) => ({
        sql: `${fn}(${args.map(txt).join(', ')})`,
        type: 'string' as const,
      });
      // A written list or object is not text; the engine hands it back as it
      // is, which a string function here cannot.
      const listy = args.some(
        (arg) => arg.type === 'array' || arg.type === 'object',
      );
      const never = (sql: string) => ({
        sql: `COALESCE(${sql}, FALSE)`,
        type: 'boolean' as const,
      });
      const math = (fn: string) => ({
        sql: `${fn}(${args.map(num).join(', ')})`,
        type: 'number' as const,
      });
      switch (name) {
        case 'lower':
        case 'toLower':
        case 'toLowerCase':
          if (listy) return decline(state, name, 'lower of a list');
          return text('LOWER');
        case 'upper':
        case 'toUpper':
        case 'toUpperCase':
          if (listy) return decline(state, name, 'upper of a list');
          // The engine upper-cases as Unicode does, sharp s to SS; Postgres
          // keeps it.
          return {
            sql: `UPPER(REPLACE(${txt(a)}, 'ß', 'SS'))`,
            type: 'string',
          };
        case 'trim':
          if (listy) return decline(state, name, 'trim of a list');
          return text('BTRIM');
        case 'len': {
          // Characters of text or of a number or boolean written as text,
          // elements of a list, fields of an object; nothing has length 0.
          const x = json(a);
          return {
            sql: `COALESCE((CASE jsonb_typeof(${x}) WHEN 'array' THEN jsonb_array_length(${x}) WHEN 'object' THEN (SELECT COUNT(*) FROM jsonb_object_keys(${x})) WHEN 'null' THEN 0 ELSE CHAR_LENGTH(${x} #>> '{}') END), 0)`,
            type: 'number',
          };
        }
        case 'startsWith':
        case 'endsWith': {
          // A run of elements is the list form's other half; only one element
          // is translated.
          if (listy) return decline(state, name, `${name} of a written list`);
          const first = name === 'startsWith';
          const onText = first
            ? `starts_with(${txt(a)}, ${txt(b)})`
            : `(RIGHT(${txt(a)}, CHAR_LENGTH(${txt(b)})) = ${txt(b)})`;
          if (a?.type === 'string') return never(onText);
          // A field may hold text or a list: which, is asked of the value.
          const x = json(a);
          return never(
            `(CASE jsonb_typeof(${x}) WHEN 'array' THEN (${x} -> ${first ? '0' : '-1'}) = ${json(b)} WHEN 'null' THEN FALSE ELSE ${onText} END)`,
          );
        }
        case 'includes': {
          if (isList(a))
            return never(
              `(${json(a)} @> jsonb_build_array(${b?.sql ?? 'NULL'}))`,
            );
          if (a?.type === 'string')
            return never(`(STRPOS(${txt(a)}, ${txt(b)}) > 0)`);
          if (listy)
            return decline(state, name, 'includes of a written object');
          const x = json(a);
          return never(
            `(CASE jsonb_typeof(${x}) WHEN 'array' THEN ${x} @> jsonb_build_array(${b?.sql ?? 'NULL'}) WHEN 'null' THEN FALSE ELSE STRPOS(${txt(a)}, ${txt(b)}) > 0 END)`,
          );
        }
        case 'abs':
          // A null counts as zero, as the engine's documentation says.
          return { sql: `COALESCE(ABS(${num(a)}), 0)`, type: 'number' };
        case 'floor':
          return math('FLOOR');
        case 'ceil':
          return math('CEIL');
        case 'sqrt':
          return math('SQRT');
        case 'sign':
          return math('SIGN');
        case 'round':
        case 'trunc':
          return {
            sql: `${name.toUpperCase()}(${num(a)}, CAST(${b ? num(b) : '0'} AS int))`,
            type: 'number',
          };
        case 'sum':
        case 'average':
        case 'mean':
        case 'min':
        case 'max': {
          const aggregate = {
            sum: 'SUM',
            average: 'AVG',
            mean: 'AVG',
            min: 'MIN',
            max: 'MAX',
          }[name];
          if (args.length === 1 && a && isList(a)) {
            const over = `(SELECT ${aggregate}(CAST(e #>> '{}' AS numeric)) FROM ${elements(a)} AS e WHERE jsonb_typeof(e) = 'number')`;
            return {
              sql: name === 'sum' ? `COALESCE(${over}, 0)` : over,
              type: 'number',
            };
          }
          if (args.some((arg) => arg.type !== 'number')) {
            return decline(
              state,
              name,
              `${name} of values that may be lists or missing`,
            );
          }
          if (name === 'min') return math('LEAST');
          if (name === 'max') return math('GREATEST');
          const total = args.map(num).join(' + ') || '0';
          return {
            sql:
              name === 'sum'
                ? `(${total})`
                : `((${total}) / ${Math.max(args.length, 1)})`,
            type: 'number',
          };
        }
        case 'year':
        case 'month':
        case 'day':
        case 'hour':
        case 'minute': {
          const part = name.toUpperCase();
          return {
            sql: `CAST(EXTRACT(${part} FROM ${utc(a)}) AS numeric)`,
            type: 'number',
          };
        }
        case 'isBefore':
          return { sql: `(${instant(a)} < ${instant(b)})`, type: 'boolean' };
        case 'isAfter':
          return { sql: `(${instant(a)} > ${instant(b)})`, type: 'boolean' };
        case 'second':
          return {
            sql: `FLOOR(EXTRACT(SECOND FROM ${utc(a)}))`,
            type: 'number',
          };
        case 'weekday':
          return {
            sql: `CAST(EXTRACT(DOW FROM ${utc(a)}) AS numeric)`,
            type: 'number',
          };
        case 'dayOfWeek':
          return { sql: `TO_CHAR(${utc(a)}, 'FMDay')`, type: 'string' };
        case 'isSameDay':
          return {
            sql: `(CAST(${utc(a)} AS date) = CAST(${utc(b)} AS date))`,
            type: 'boolean',
          };
        case 'toDate':
          return { sql: `TO_CHAR(${utc(a)}, 'YYYY-MM-DD')`, type: 'string' };
        case 'gteDateOnly':
        case 'lteDateOnly':
        case 'gtDateOnly':
        case 'ltDateOnly': {
          // As the engine reads a day for these: text or a date, never a
          // number, and unknown - NULL, as SQL has it - when either side
          // has none.
          if (a?.type === 'number' || b?.type === 'number')
            return { sql: 'CAST(NULL AS boolean)', type: 'boolean' };
          const op = {
            gteDateOnly: '>=',
            lteDateOnly: '<=',
            gtDateOnly: '>',
            ltDateOnly: '<',
          }[name];
          return {
            sql: `(CAST(${utc(a)} AS date) ${op} CAST(${utc(b)} AS date))`,
            type: 'boolean',
          };
        }
        case 'addDays':
        case 'addMonths':
        case 'addYears':
        case 'addHours':
        case 'addMinutes': {
          // Whole units, as the engine takes them, added in UTC so that a
          // month from 31 January is 29 February whatever the session's zone.
          const unit = {
            addDays: 'day',
            addMonths: 'month',
            addYears: 'year',
            addHours: 'hour',
            addMinutes: 'minute',
          }[name];
          return {
            // A missing count adds nothing, as the engine takes it.
            sql: `((${utc(a)} + COALESCE(TRUNC(${num(b)}), 0) * INTERVAL '1 ${unit}') AT TIME ZONE 'UTC')`,
            type: 'datetime',
          };
        }
        case 'diffDays':
        case 'diffHours':
        case 'diffMinutes':
        case 'daysBetween':
        case 'hoursBetween': {
          // Whole units elapsed, towards zero; `…Between` counts from its first
          // argument to its second, `diff…` the other way.
          const seconds = {
            diffDays: 86400,
            daysBetween: 86400,
            diffHours: 3600,
            hoursBetween: 3600,
            diffMinutes: 60,
          }[name];
          const [later, earlier] = name.endsWith('Between') ? [b, a] : [a, b];
          return {
            sql: `TRUNC(EXTRACT(EPOCH FROM (${instant(later)} - ${instant(earlier)})) / ${seconds})`,
            type: 'number',
          };
        }
        case 'monthsBetween':
          return {
            sql: `((EXTRACT(YEAR FROM ${utc(b)}) - EXTRACT(YEAR FROM ${utc(a)})) * 12 + EXTRACT(MONTH FROM ${utc(b)}) - EXTRACT(MONTH FROM ${utc(a)}))`,
            type: 'number',
          };
        case 'yearsBetween':
          return {
            sql: `EXTRACT(YEAR FROM AGE(CAST(${utc(b)} AS date), CAST(${utc(a)} AS date)))`,
            type: 'number',
          };
        case 'startOfDay':
        case 'startOfMonth': {
          const field = name === 'startOfDay' ? 'day' : 'month';
          return {
            sql: `(DATE_TRUNC('${field}', ${utc(a)}) AT TIME ZONE 'UTC')`,
            type: 'datetime',
          };
        }
        case 'endOfMonth':
          return {
            sql: `((DATE_TRUNC('month', ${utc(a)}) + INTERVAL '1 month' - INTERVAL '1 second') AT TIME ZONE 'UTC')`,
            type: 'datetime',
          };
        case 'pow':
          return { sql: `POWER(${num(a)}, ${num(b)})`, type: 'number' };
        default:
          // Everything else is declined rather than guessed at: each case above
          // is here because the parity harness shows it answering what the
          // engine answers.
          void c;
          return decline(
            state,
            name,
            `Function '${name}' is not translated for Postgres yet`,
          );
      }
    },
    arrayLiteral(items) {
      if (items.length === 0) return `'[]'::jsonb`;
      return `jsonb_build_array(${items.map((item) => item.sql).join(', ')})`;
    },
    coalesce(left, right) {
      // A `jsonb` null is nothing too, and both sides have to be one type.
      if (right.type === 'string')
        return `COALESCE(${txt(left)}, ${right.sql})`;
      if (right.type === 'number')
        return `COALESCE(${num(left)}, ${right.sql})`;
      return `COALESCE(NULLIF(${json(left)}, 'null'::jsonb), ${json(right)})`;
    },
    conditional(branches, otherwise) {
      const when = branches
        .map((branch) => `WHEN ${branch.when.sql} THEN ${branch.result.sql}`)
        .join(' ');
      return `(CASE ${when} ELSE ${otherwise.sql} END)`;
    },
  };
  return pg;
}

function createSurrealAdapter(): ExpressionQueryAdapter {
  const surreal: SqlAdapter = {
    kind: 'surreal',
    family: 'nosql',
    defaultReferences: { root: 'root', value: 'value', ctx: 'ctx' },
    createParameter(state, value) {
      if (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        // Money: an amount the engine keeps apart from bare numbers, which
        // SurrealQL has no way to.
        createUnsupportedFeature(state, {
          code: 'unsupported-expression',
          message:
            'money and other structured literals are not translated to SurrealQL',
          nodeKind: 'literal',
        });
      }
      const name = `p${Object.keys(state.namedParams).length}`;
      state.namedParams[name] = value;
      state.parameterOrder.push(name);
      return `$${name}`;
    },
    quoteString(value) {
      return `'${value.replace(/'/g, "\\'")}'`;
    },
    accessPath(baseSql, segments) {
      return segments.reduce<string>((current, segment) => {
        if (Number.isInteger(segment)) return `${current}[${String(segment)}]`;
        // `first` and `last` are members of a value in this language and
        // functions in SurrealQL. Written as a path they are a projection -
        // `[1,2,3].first` answers `[NONE, NONE, NONE]`, one member per element -
        // which is a wrong answer rather than an error.
        if (segment === 'first') return `array::first(${current})`;
        if (segment === 'last') return `array::last(${current})`;
        return `${current}.${String(segment)}`;
      }, baseSql);
    },
    nullCheck(sql, equals) {
      return equals
        ? `((${sql}) IS NONE OR (${sql}) IS NULL)`
        : `((${sql}) IS NOT NONE AND (${sql}) IS NOT NULL)`;
    },
    concat(parts) {
      return `string::concat(${parts.map((part) => part.sql).join(', ')})`;
    },
    binary(op, left, right) {
      if (op === '&&') return `(${left.sql} AND ${right.sql})`;
      if (op === '||') return `(${left.sql} OR ${right.sql})`;
      // SurrealDB divides two integers as integers: `10 / 3` is 3, where the
      // expression language answers 3.333…. A rate computed in the database
      // would be quietly wrong, so the left side is widened first.
      if (op === '/') return `((<float> ${left.sql}) / ${right.sql})`;
      return `(${left.sql} ${op === '==' ? '=' : op === '!=' ? '!=' : op} ${right.sql})`;
    },
    inList(left, right, equals) {
      return equals
        ? `(${left.sql} IN ${right.sql})`
        : `(${left.sql} NOT IN ${right.sql})`;
    },
    functionCall(name, args, state) {
      const alias =
        name === 'toLower'
          ? 'lower'
          : name === 'toUpper'
            ? 'upper'
            : name === 'mean'
              ? 'average'
              : name;
      // Asked before the switch, not only after it. A name with a case below is
      // translated by that case, so a function listed as unsupported was
      // translated anyway - the list only ever governed names nothing handled,
      // which is the opposite of what a list of exclusions is for.
      const excluded =
        SURREAL_UNSUPPORTED_FUNCTIONS.get(name) ??
        SURREAL_UNSUPPORTED_FUNCTIONS.get(alias);
      if (excluded) {
        state.unsupported.push({
          code: 'unsupported-function',
          message: `${name} cannot be translated to SurrealQL: ${excluded}`,
          adapter: 'surreal',
          functionName: name,
        });
        return { sql: 'NONE', type: 'unknown' };
      }
      // What the engine answers for nothing, text read from a number, a list
      // where text was expected - SurrealQL's functions refuse all three with
      // an error, and one record with a null field then fails the whole query.
      const at = (i: number) => args[i]?.sql ?? 'NONE';
      const nothing = (x: string) => `(${x} = NONE OR ${x} = NULL)`;
      const asText = (x: string) =>
        `(IF type::is_string(${x}) THEN ${x} ELSE <string> ${x} END)`;
      // A string result that reads as a number is that number, as the engine
      // reads it: `trim(" 42 ")` is 42. Not with a leading zero: "007" stays.
      const derived = (r: string) =>
        `(IF string::is_numeric(${r}) AND !(string::starts_with(${r}, '0') AND string::len(${r}) > 1 AND !string::starts_with(${r}, '0.')) THEN <number> ${r} ELSE ${r} END)`;
      const textFn = (fn: string) => {
        const x = at(0);
        return {
          sql: `(IF ${nothing(x)} THEN NULL ELSE IF type::is_string(${x}) THEN ${derived(`${fn}(${x})`)} ELSE IF type::is_bool(${x}) THEN ${fn}(<string> ${x}) ELSE ${x} END)`,
          type: 'unknown' as const,
        };
      };
      const listed = args.length === 1 && args[0]?.type === 'array';
      // The numbers among the arguments, lists opened, as the engine collects
      // them: nulls and anything not a number are passed over.
      const numbers = () =>
        `array::filter(array::flatten([${args.map((arg) => arg.sql).join(', ')}]), |$v| type::is_number($v))`;
      void listed;
      // A date as SurrealQL holds one, or NONE where the engine has none: a
      // datetime, milliseconds since 1970, or text that is a date - a time
      // without an offset read as UTC, as the engine reads it. A bare cast
      // raises an error on text that is not a date, and fails the query.
      const dt = (x: string) =>
        `(IF type::is_datetime(${x}) THEN ${x} ELSE IF type::is_number(${x}) THEN time::from_millis(<int> ${x}) ELSE IF type::is_string(${x}) AND string::is_datetime(${x}) THEN <datetime> ${x} ELSE IF type::is_string(${x}) AND string::is_datetime(${x} + 'Z') THEN <datetime> (${x} + 'Z') ELSE IF type::is_string(${x}) AND string::is_datetime(${x} + ':00Z') THEN <datetime> (${x} + ':00Z') ELSE NONE END)`;
      const noDate = (x: string) => `(${dt(x)} = NONE)`;
      const part = (fn: string) => ({
        sql: `(IF ${noDate(at(0))} THEN NULL ELSE ${fn}(${dt(at(0))}) END)`,
        type: 'number' as const,
      });
      const day = (x: string) => `time::floor(${dt(x)}, 1d)`;
      // Whole units from `earlier` to `later`, towards zero; a duration is
      // never negative, so the sign is taken from which came first.
      const elapsed = (later: string, earlier: string, fn: string) =>
        `(IF ${noDate(later)} OR ${noDate(earlier)} THEN NULL ELSE IF ${dt(later)} >= ${dt(earlier)} THEN ${fn}(${dt(later)} - ${dt(earlier)}) ELSE -${fn}(${dt(earlier)} - ${dt(later)}) END)`;
      switch (alias) {
        case 'year':
          return part('time::year');
        case 'month':
          return part('time::month');
        case 'day':
          return part('time::day');
        case 'hour':
        case 'hours':
          return part('time::hour');
        case 'minute':
        case 'minutes':
          return part('time::minute');
        case 'second':
        case 'seconds':
          return part('time::second');
        case 'weekday':
          // SurrealQL counts Sunday as 7; the engine as 0.
          return {
            sql: `(IF ${noDate(at(0))} THEN NULL ELSE time::wday(${dt(at(0))}) % 7 END)`,
            type: 'number',
          };
        case 'dayOfWeek':
          return {
            sql: `(IF ${noDate(at(0))} THEN NULL ELSE time::format(${dt(at(0))}, '%A') END)`,
            type: 'string',
          };
        case 'toDate':
          return {
            sql: `(IF ${noDate(at(0))} THEN NULL ELSE time::format(${dt(at(0))}, '%Y-%m-%d') END)`,
            type: 'string',
          };
        case 'isBefore':
        case 'isAfter':
        case 'isSameDay': {
          const [x, y] = [at(0), at(1)];
          const holds =
            alias === 'isSameDay'
              ? `${day(x)} = ${day(y)}`
              : `${dt(x)} ${alias === 'isBefore' ? '<' : '>'} ${dt(y)}`;
          return {
            sql: `(IF ${noDate(x)} OR ${noDate(y)} THEN NULL ELSE ${holds} END)`,
            type: 'boolean',
          };
        }
        case 'gteDateOnly':
        case 'lteDateOnly':
        case 'gtDateOnly':
        case 'ltDateOnly': {
          // A day of text or a date, never of a number; unknown when either
          // side has none.
          const [x, y] = [at(0), at(1)];
          const op = {
            gteDateOnly: '>=',
            lteDateOnly: '<=',
            gtDateOnly: '>',
            ltDateOnly: '<',
          }[alias];
          return {
            sql: `(IF type::is_number(${x}) OR type::is_number(${y}) OR ${noDate(x)} OR ${noDate(y)} THEN NULL ELSE ${day(x)} ${op} ${day(y)} END)`,
            type: 'boolean',
          };
        }
        case 'addDays':
        case 'addHours':
        case 'addMinutes': {
          const [x, n] = [at(0), at(1)];
          const from = {
            addDays: 'duration::from_days',
            addHours: 'duration::from_hours',
            addMinutes: 'duration::from_mins',
          }[alias];
          // Whole units; a missing count adds nothing, and a negative one is
          // taken away, since a duration cannot be negative.
          const count = `(IF ${nothing(n)} THEN 0 ELSE IF ${n} < 0 THEN math::ceil(${n}) ELSE math::floor(${n}) END)`;
          return {
            sql: `(IF ${noDate(x)} THEN NULL ELSE IF ${count} < 0 THEN ${dt(x)} - ${from}(<int> -${count}) ELSE ${dt(x)} + ${from}(<int> ${count}) END)`,
            type: 'datetime',
          };
        }
        case 'diffDays':
          return {
            sql: elapsed(at(0), at(1), 'duration::days'),
            type: 'number',
          };
        case 'diffHours':
          return {
            sql: elapsed(at(0), at(1), 'duration::hours'),
            type: 'number',
          };
        case 'diffMinutes':
          return {
            sql: elapsed(at(0), at(1), 'duration::mins'),
            type: 'number',
          };
        case 'daysBetween':
          return {
            sql: elapsed(at(1), at(0), 'duration::days'),
            type: 'number',
          };
        case 'hoursBetween':
          return {
            sql: elapsed(at(1), at(0), 'duration::hours'),
            type: 'number',
          };
        case 'monthsBetween': {
          const [x, y] = [at(0), at(1)];
          return {
            sql: `(IF ${noDate(x)} OR ${noDate(y)} THEN NULL ELSE (time::year(${dt(y)}) - time::year(${dt(x)})) * 12 + time::month(${dt(y)}) - time::month(${dt(x)}) END)`,
            type: 'number',
          };
        }
        case 'startOfDay':
          return {
            sql: `(IF ${noDate(at(0))} THEN NULL ELSE ${day(at(0))} END)`,
            type: 'datetime',
          };

        case 'len': {
          const x = at(0);
          return {
            sql: `(IF ${nothing(x)} THEN 0 ELSE IF type::is_array(${x}) THEN array::len(${x}) ELSE IF type::is_object(${x}) THEN array::len(object::keys(${x})) ELSE string::len(<string> ${x}) END)`,
            type: 'number',
          };
        }
        case 'lower':
          return textFn('string::lowercase');
        case 'upper':
          return textFn('string::uppercase');
        case 'trim':
          return textFn('string::trim');
        case 'substring': {
          const x = at(0);
          const from = `((${args[1]?.sql ?? '1'}) - 1)`;
          // `string::slice` takes where to stop, not how many: the language's
          // inclusive one-based end is SurrealQL's exclusive zero-based one.
          const count = args[2]
            ? `(IF ${nothing(args[2].sql)} THEN string::len(<string> ${x}) ELSE ${args[2].sql} END)`
            : undefined;
          const sliced = count
            ? `string::slice(${asText(x)}, ${from}, ${count})`
            : `string::slice(${asText(x)}, ${from})`;
          return {
            sql: `(IF ${nothing(x)} THEN NULL ELSE ${derived(sliced)} END)`,
            type: 'unknown',
          };
        }
        case 'includes': {
          const [x, needle] = [at(0), at(1)];
          return {
            sql: `(IF ${nothing(x)} THEN false ELSE IF type::is_array(${x}) THEN (${x} CONTAINS ${needle}) ELSE IF ${nothing(needle)} THEN false ELSE string::contains(<string> ${x}, <string> ${needle}) END)`,
            type: 'boolean',
          };
        }
        case 'startsWith':
        case 'endsWith': {
          const [x, part] = [at(0), at(1)];
          const first = alias === 'startsWith';
          const listEnd = first
            ? `(IF type::is_array(${part}) THEN array::slice(${x}, 0, array::len(${part})) = ${part} ELSE array::first(${x}) = ${part} END)`
            : `(IF type::is_array(${part}) THEN (array::len(${part}) <= array::len(${x}) AND array::slice(${x}, array::len(${x}) - array::len(${part})) = ${part}) ELSE array::last(${x}) = ${part} END)`;
          const textEnd = `string::${first ? 'starts_with' : 'ends_with'}(<string> ${x}, <string> ${part})`;
          return {
            sql: `(IF ${nothing(x)} OR ${nothing(part)} THEN false ELSE IF type::is_array(${x}) THEN ${listEnd} ELSE ${textEnd} END)`,
            type: 'boolean',
          };
        }
        case 'abs':
          // A null counts as zero, as the engine's documentation says.
          return {
            sql: `(IF ${nothing(at(0))} THEN 0 ELSE math::abs(${at(0)}) END)`,
            type: 'number',
          };
        case 'floor':
          return { sql: `math::floor(${at(0)})`, type: 'number' };
        case 'ceil':
          return { sql: `math::ceil(${at(0)})`, type: 'number' };
        case 'min':
          return { sql: `math::min(${numbers()})`, type: 'number' };
        case 'max':
          return { sql: `math::max(${numbers()})`, type: 'number' };
        case 'sum':
          return { sql: `math::sum(${numbers()})`, type: 'number' };
        case 'average':
          return {
            sql: `(IF array::len(${numbers()}) = 0 THEN NULL ELSE math::mean(${numbers()}) END)`,
            type: 'number',
          };
        case 'now':
          return { sql: `time::now()`, type: 'datetime' };
        default: {
          const reason =
            SURREAL_UNSUPPORTED_FUNCTIONS.get(name) ??
            SURREAL_UNSUPPORTED_FUNCTIONS.get(alias);
          if (reason) {
            createUnsupportedFeature(state, {
              code: 'unsupported-function',
              message: reason,
              functionName: name,
              nodeKind: 'call',
            });
          }
          return undefined;
        }
      }
    },
    arrayLiteral(items) {
      return `[${items.map((item) => item.sql).join(', ')}]`;
    },
    coalesce(left, right) {
      // SurrealQL has no COALESCE. `??` is the operator for it, and it answers
      // the right-hand side for NONE and NULL alike, which is what the
      // expression language means by it.
      return `(${left.sql} ?? ${right.sql})`;
    },
    conditional(branches, otherwise) {
      // `IF … THEN … ELSE … END`, not `CASE WHEN`: SurrealDB's parser refuses
      // the second outright.
      const when = branches
        .map(
          (branch, index) =>
            `${index === 0 ? 'IF' : 'ELSE IF'} ${branch.when.sql} THEN ${branch.result.sql}`,
        )
        .join(' ');
      return `(${when} ELSE ${otherwise.sql} END)`;
    },
  };
  return surreal;
}

/** The Postgres adapter. Prefer the `postgresQueryAdapter` spelling. */
export const postgresSqlAdapter = createPostgresAdapter();
/** The SurrealDB adapter. Prefer the `surrealQueryAdapter` spelling. */
export const surrealSqlAdapter = createSurrealAdapter();
/** The neutral spelling of the Postgres adapter. */
export const postgresQueryAdapter = postgresSqlAdapter;
/** The neutral spelling of the SurrealDB adapter. */
export const surrealQueryAdapter = surrealSqlAdapter;

/** A Postgres adapter with some of its behaviour replaced. Custom function translators are MERGED with the defaults rather than replacing them. */
export function createPostgresQueryAdapter(
  overrides: Partial<ExpressionQueryAdapter> = {},
): ExpressionQueryAdapter {
  return {
    ...postgresSqlAdapter,
    ...overrides,
    customFunctionTranslators: {
      ...postgresSqlAdapter.customFunctionTranslators,
      ...overrides.customFunctionTranslators,
    },
    unsupportedFunctions:
      overrides.unsupportedFunctions ?? postgresSqlAdapter.unsupportedFunctions,
  };
}

/** A SurrealDB adapter with some of its behaviour replaced, merging translators as {@link createPostgresQueryAdapter} does. */
export function createSurrealQueryAdapter(
  overrides: Partial<ExpressionQueryAdapter> = {},
): ExpressionQueryAdapter {
  return {
    ...surrealSqlAdapter,
    ...overrides,
    customFunctionTranslators: {
      ...surrealSqlAdapter.customFunctionTranslators,
      ...overrides.customFunctionTranslators,
    },
    unsupportedFunctions:
      overrides.unsupportedFunctions ?? surrealSqlAdapter.unsupportedFunctions,
  };
}

function resolveAdapter(
  adapter?: QueryTargetKind | ExpressionQueryAdapter,
): ExpressionQueryAdapter {
  if (!adapter || adapter === 'surreal') return surrealSqlAdapter;
  if (adapter === 'postgres') return postgresSqlAdapter;
  return adapter;
}

/**
 * Compile a Crown expression into query text for a database.
 *
 * What lets a rule written once be enforced in the form AND used to filter rows
 * at the source, without the condition being written twice in two languages.
 *
 * NEVER THROWS ON AN UNTRANSLATABLE EXPRESSION. Read `unsupported` on the result
 * and fall back to evaluating in the application when it is non-empty - the
 * query text is only trustworthy when it is empty.
 */
export function compileExpressionToQuery(
  source: string,
  options: CompileExpressionToSqlOptions = {},
): SqlCompilationResult {
  const adapter = resolveAdapter(options.adapter);
  const customFunctionTranslators = buildCustomFunctionTranslators(options);
  const parsed = parseExpressionDetailed(source, {
    allowedFunctions: {
      // The engine's names, as a map, because that is the shape the parser's
      // option takes. Nothing calls these - the parser only asks whether a name
      // is there - so a marker value is honest about what they are.
      ...Object.fromEntries(
        builtinFunctionNames().map((name): [string, ExpressionFunction] => [
          name,
          () => null,
        ]),
      ),
      ...getAllCustomFunctions(),
      ...Object.fromEntries(
        Object.keys(customFunctionTranslators).map(
          (name): [string, ExpressionFunction] => [name, () => null],
        ),
      ),
    },
  });
  const state: EmitState = {
    adapter,
    diagnostics: parsed.diagnostics,
    unsupported: [],
    references: {
      ...adapter.defaultReferences,
      ...options.references,
    },
    strict: options.strict ?? false,
    positionalParams: [],
    namedParams: {},
    parameterOrder: [],
    customFunctionTranslators,
  };

  if (
    !parsed.ast ||
    parsed.diagnostics.some((diagnostic) => diagnostic.severity === 'error')
  ) {
    createUnsupportedFeature(state, {
      code: 'parse-error',
      message:
        'Expression has parse errors and cannot be translated to a query expression',
    });
    return {
      source,
      sql: 'NULL',
      ast: parsed.ast,
      diagnostics: parsed.diagnostics,
      unsupported: state.unsupported,
      supported: false,
      parameters:
        adapter.kind === 'postgres'
          ? state.positionalParams
          : state.namedParams,
      parameterOrder: state.parameterOrder,
      adapter: adapter.kind,
    };
  }

  const sql = emitNode(parsed.ast, state, new Map(), 'unknown');
  const supported = state.unsupported.length === 0;

  return {
    source,
    sql: sql.sql,
    ast: parsed.ast,
    diagnostics: parsed.diagnostics,
    unsupported: state.unsupported,
    supported,
    parameters:
      adapter.kind === 'postgres' ? state.positionalParams : state.namedParams,
    parameterOrder: state.parameterOrder,
    adapter: adapter.kind,
  };
}

/** The older spelling of {@link compileExpressionToQuery}. */
export function compileExpressionToSql(
  source: string,
  options: CompileExpressionToSqlOptions = {},
): SqlCompilationResult {
  return compileExpressionToQuery(source, options);
}

/** What one target can translate, for deciding up front rather than compiling and inspecting the result. */
export function getQueryTranslationSupport(
  adapter: QueryTargetKind | ExpressionQueryAdapter,
): QueryTranslationSupportReport {
  const resolved = resolveAdapter(adapter);
  const unsupportedReasons =
    resolved.unsupportedFunctions ??
    (resolved.kind === 'postgres'
      ? POSTGRES_UNSUPPORTED_FUNCTIONS
      : SURREAL_UNSUPPORTED_FUNCTIONS);
  const unsupportedNodeKinds =
    resolved.kind === 'postgres'
      ? POSTGRES_UNSUPPORTED_NODE_KINDS
      : SURREAL_UNSUPPORTED_NODE_KINDS;
  const unsupportedFunctions: Array<{ name: string; reason: string }> = [];
  const supportedFunctions: string[] = [];
  const customFunctions = new Set(
    Object.keys(resolved.customFunctionTranslators ?? {}),
  );
  const functionNames = Array.from(
    new Set([
      ...getAllKnownFunctionNames(resolved.customFunctionTranslators ?? {}),
      ...customFunctions,
    ]),
  ).sort();

  for (const name of functionNames) {
    if (customFunctions.has(name)) {
      supportedFunctions.push(name);
      continue;
    }
    const reason = unsupportedReasons.get(name);
    if (reason) unsupportedFunctions.push({ name, reason });
    else supportedFunctions.push(name);
  }

  return {
    adapter: resolved.kind,
    family: resolved.family,
    supportedFunctions,
    unsupportedFunctions,
    unsupportedNodeKinds,
  };
}

/** The older spelling of {@link getQueryTranslationSupport}. */
export function getSqlTranslationSupport(
  adapter: SqlDialect,
): SqlSupportReport {
  return getQueryTranslationSupport(adapter);
}

/** The older spelling of {@link getAllQueryTranslationSupport}. */
export function getAllSqlTranslationSupport(): SqlSupportReport[] {
  return [
    getSqlTranslationSupport('surreal'),
    getSqlTranslationSupport('postgres'),
  ];
}

/** Translation support for every target, for comparing them side by side. */
export function getAllQueryTranslationSupport(): QueryTranslationSupportReport[] {
  return [
    getQueryTranslationSupport('surreal'),
    getQueryTranslationSupport('postgres'),
  ];
}
